import React from 'react';
import { Form } from 'react-bootstrap';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import { useGetMessageSoundsQuery } from '../../app/services/chatbot-rtk.services';
import PopoverWithAudio, { IPopoverWithAudioType } from '../../components/common/PopoverWithAudio';
import ProBadge from '../../components/ui/ProBadge';
import usePermission from '../../hooks/usePermission';
import { handleResetAppearance, handleUpdateAppearanceItem, selectAppearanceState } from './appearanceSlice';
import ColorDropDown from './ColorDropDown';
import { IAiAppearance } from './types';

type Props = {
  register: UseFormRegister<IAiAppearance>;
  watch: UseFormWatch<IAiAppearance>;
  errors: FieldErrors<IAiAppearance>;
  control: Control<IAiAppearance, any>;
};

function AppearanceMessageNotificationComponent({ register, watch, errors, control }: Props) {
  const permissions = usePermission();
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();

  const appearanceState = useAppSelector(selectAppearanceState);

  const { data, isSuccess, isLoading } = useGetMessageSoundsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const voiceResponsePopData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-10'),
  };

  const handlePlaySelectedSound = () => {
    if (!watch('notificationSound')) {
      return;
    }

    const audio = new Audio(watch('notificationSound'));

    audio.play();
  };

  return (
    <React.Fragment>
      <div className={'tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex'}>
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
          <Form.Group className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
            <Form.Check
              disabled={!permissions.settingsPage.widget.customization.notificationBadge}
              type="switch"
              id="notificationMessage"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t('DashboardSettingCustomizeChat/l-13')}
              {...register('isNotificationMessageActive', {
                onChange: (e: any) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: 'isNotificationMessageActive',
                      value: e.target.checked,
                    })
                  );
                },
              })}
            />
            {!permissions.settingsPage.widget.customization.notificationBadge && (
              <ProBadge
                className="tw-ms-2"
                onClose={() => {
                  dispatch(handleResetAppearance());
                }}
              />
            )}
            <PopoverWithAudio
              data={{
                contentText: voiceResponsePopData.contentText,
              }}
            />
          </Form.Group>
        </div>
      </div>
      {watch('isNotificationMessageActive') && (
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
          <Form.Group className={`tw-w-full`}>
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              Message
              {!permissions.settingsPage.widget.customization.notificationMessage && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
            </Form.Label>

            <Form.Control
              placeholder="Hello! 👋"
              max={13}
              maxLength={13}
              disabled={!permissions.settingsPage.widget.customization.notificationMessage}
              className="tw-w-full tw-col-span-12"
              isInvalid={!!errors?.notificationMessage && watch('isNotificationMessageActive') && watch('isNotificationMessageActive') === true}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              {...register('notificationMessage', {
                onChange: (e: any) => {
                  const value = e.target.value.replace(/^\s+/, '');
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: 'notificationMessage',
                      value: value,
                    })
                  );
                },
              })}
            />

            {watch('isNotificationMessageActive') && watch('isNotificationMessageActive') === true && (
              <Form.Control.Feedback type="invalid">{errors.notificationMessage?.message}</Form.Control.Feedback>
            )}
          </Form.Group>

          {appearanceState?.bot_code !== BotTypeEnum.CALLBOT && (
            <Form.Group className={`tw-w-full tw-col-span-12  tw-mb-4`}>
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('DashboardSettingCustomizeChat/t-01')}
              </Form.Label>
              <Controller
                control={control}
                name="notificationMessageTextColor"
                render={({ field: { onChange, value } }) => (
                  <ColorDropDown
                    acceptableColors={[
                      { label: 'White', value: '#FFFFFF' },
                      { label: 'Grey', value: '#6B7280' },
                      { label: 'Red', value: '#EF4444' },
                      { label: 'Orange', value: '#F97316' },
                      { label: 'Yellow', value: '#F59E0B' },
                      { label: 'Green', value: '#10B981' },
                      { label: 'Teal', value: '#14B8A6' },
                      { label: 'Cyan', value: '#06B6D4' },
                      { label: 'Light Blue', value: '#0EA5E9' },
                      { label: 'Blue', value: '#3B82F6' },
                      { label: 'Indigo', value: '#6366F1' },
                      { label: 'Purple (Violet)', value: '#8B5CF6' },
                      { label: 'Pink', value: '#EC4899' },
                    ]}
                    isInvalid={!!errors.notificationMessageTextColor}
                    errorMessage={errors.notificationMessageTextColor?.message}
                    onColorChanged={color => {
                      onChange(color);

                      dispatch(
                        handleUpdateAppearanceItem({
                          key: 'notificationMessageTextColor',
                          value: color,
                        })
                      );
                    }}
                    currentColor={value ?? ''}
                  />
                )}
              />
            </Form.Group>
          )}
        </div>
      )}
    </React.Fragment>
  );
}

export default WrappedErrorBoundary(AppearanceMessageNotificationComponent);
